'use client'

import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { useGetUserPromptQuery, useUpdateUserPromptMutation } from '@/lib/features/user/userApi'
import { cn } from '@/lib/utils'
import { useEffect, useState } from 'react'
import { IoMdClose } from 'react-icons/io'
import { toast } from 'react-toastify'
import Modal from '../Modal/Modal'

const MAX_CHARS = 1000

interface PersonalizationPanelProps {
  isOpen: boolean
  onClose: () => void
}

export default function PersonalizationPanel({ isOpen, onClose }: PersonalizationPanelProps) {
  const [userPrompt, setUserPrompt] = useState('')
  const currentBusinessProfileId = useCurrentBusinessProfileId()
  const {
    data: userPromptData,
    isLoading,
    refetch,
  } = useGetUserPromptQuery(
    {
      businessProfileId: currentBusinessProfileId || undefined,
    },
    {
      skip: !isOpen || !currentBusinessProfileId,
    }
  )
  const [updateUserPrompt, { isLoading: isUpdatingPrompt }] = useUpdateUserPromptMutation()

  // Initialize user prompt when personalization panel opens
  useEffect(() => {
    if (isOpen) {
      if (userPromptData?.userPrompt) {
        setUserPrompt(userPromptData.userPrompt)
      } else {
        setUserPrompt('')
      }
    }
  }, [isOpen, userPromptData])

  // Refetch user prompt when business profile ID changes
  useEffect(() => {
    if (isOpen && currentBusinessProfileId) {
      refetch()
    }
  }, [currentBusinessProfileId, isOpen, refetch])

  const handleSaveUserPrompt = async () => {
    try {
      await updateUserPrompt({
        userPrompt,
        businessProfileId: currentBusinessProfileId || undefined,
      }).unwrap()
      toast.success('User prompt updated successfully')
      onClose()
    } catch (error: any) {
      console.error('Failed to update user prompt:', error)
      const errorMessage = error?.data?.error || error?.message || 'Failed to update user prompt'
      toast.error(errorMessage)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="font-hanken-grotesk relative flex w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
    >
      {/* Header */}
      <div className="border-b border-gray-100 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-[#222]">Personalization</h2>
            <p className="text-sm text-gray-500">Customize your AI experience</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close personalization"
          >
            <IoMdClose size={24} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-[60vh] overflow-y-auto px-8 py-6">
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-40 w-full animate-pulse rounded-xl bg-gray-200" />
          </div>
        ) : (
          <label className="block">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-sm font-semibold text-[#222]">User Context</span>
            </div>
            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              Help the AI understand your role, industry, and preferences for more relevant and
              personalized responses.
            </p>
            <textarea
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              maxLength={MAX_CHARS}
              rows={8}
              className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-[15px] leading-relaxed text-[#222] placeholder-gray-400 shadow-sm transition-all focus:border-gray-400 focus:shadow-md focus:outline-none"
              placeholder="e.g., I'm a product manager at a B2B SaaS company focused on marketing automation. I'm interested in growth strategies and data-driven decision making..."
            />
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>This helps tailor responses to your needs</span>
              </div>
              <div
                className={cn(
                  'text-xs font-medium transition-colors',
                  userPrompt.length >= MAX_CHARS - 10
                    ? userPrompt.length >= MAX_CHARS
                      ? 'text-red-500'
                      : 'text-orange-500'
                    : 'text-gray-500'
                )}
              >
                {userPrompt.length}/{MAX_CHARS}
              </div>
            </div>
          </label>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 px-8 py-5">
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border-2 border-gray-200 px-5 py-2.5 text-sm font-medium text-[#222] transition-all hover:border-gray-300 hover:bg-gray-50 active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveUserPrompt}
            disabled={isUpdatingPrompt || userPrompt.length > MAX_CHARS}
            className="rounded-lg bg-[#222] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-[#444] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUpdatingPrompt ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}
